import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { signInAnonymously } from "firebase/auth";
import { ref, uploadBytesResumable } from "firebase/storage";
import { nanoid } from "nanoid";
import { Camera, Images, Check, Upload, AlertTriangle, AlertCircle, RotateCcw, ImagePlus } from "lucide-react";

import { auth, storage } from "../../firebase.js";
import { resolveUploadSession, attachUploadedPhotos } from "../../uploadSessions.js";
import { validateImageFile } from "../../imageValidation.js";
import Logo from "../../components/Logo.js";
import Message from "../../components/Message.js";

function UploadPhotos() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | invalid | ready | error
  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]); // { key, preview, progress, state, error }
  const [linkExpired, setLinkExpired] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }
        const result = await resolveUploadSession(token);
        if (cancelled) return;
        if (!result || !result.valid) {
          setStatus("invalid");
          return;
        }
        setSession(result);
        setStatus("ready");
      } catch (err) {
        console.error(err);
        if (!cancelled) setStatus("error");
      }
    }
    init();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const updateItem = useCallback((key, patch) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }, []);

  const uploadFile = useCallback(
    async (file, venueId) => {
      const key = nanoid(8);
      const preview = URL.createObjectURL(file);
      setItems((prev) => [...prev, { key, file, preview, progress: 0, state: "checking", error: "" }]);

      const check = await validateImageFile(file);
      // check.width === 0 betekent dat de browser het bestand niet kon decoderen
      // (bijv. HEIC van een iPhone). Dat weigeren we niet, we uploaden gewoon.
      if (!check.ok && check.width > 0) {
        updateItem(key, { state: "rejected", error: check.error });
        return;
      }

      const id = nanoid(10).toLowerCase();
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const task = uploadBytesResumable(ref(storage, `venues/${venueId}/images/${id}.${ext}`), file);
      updateItem(key, { state: "uploading" });

      task.on(
        "state_changed",
        (snapshot) => {
          updateItem(key, { progress: Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100) });
        },
        (err) => {
          console.error(err);
          updateItem(key, { state: "error", error: "Er ging iets mis tijdens het uploaden. Controleer uw internetverbinding." });
        },
        async () => {
          try {
            await attachUploadedPhotos(token, [id]);
            updateItem(key, { state: "done", progress: 100 });
          } catch (err) {
            console.error(err);
            if (err.status === 410) {
              setLinkExpired(true);
              updateItem(key, { state: "error", error: "De uploadlink is verlopen. Vraag de locatie-eigenaar om een nieuwe link." });
            } else {
              updateItem(key, { state: "error", error: "Toevoegen aan de locatie is mislukt." });
            }
          }
        },
      );
    },
    [token, updateItem],
  );

  const onFilesSelected = useCallback(
    (fileList) => {
      if (!session || linkExpired) return;
      const files = Array.from(fileList || []);
      files.forEach((file) => uploadFile(file, session.venueId));
    },
    [session, uploadFile, linkExpired],
  );

  const removeItem = useCallback((key) => {
    setItems((prev) => {
      const target = prev.find((it) => it.key === key);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((it) => it.key !== key);
    });
  }, []);

  const retryItem = useCallback(
    (key) => {
      if (linkExpired) return;
      const target = items.find((it) => it.key === key);
      if (!target?.file || !session) return;
      removeItem(key);
      uploadFile(target.file, session.venueId);
    },
    [items, session, uploadFile, removeItem, linkExpired],
  );

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg-base">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-text-secondary">Laden...</p>
        </div>
      </div>
    );
  }

  if (status === "invalid" || status === "error") {
    return (
      <div className="min-h-screen bg-bg-base flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="mb-6 flex justify-center">
            <Logo />
          </div>
          <Message
            variant={status === "invalid" ? "warning" : "error"}
            text={
              status === "invalid"
                ? "Deze uploadlink is verlopen of ongeldig. Vraag de locatie-eigenaar om een nieuwe link."
                : "Er ging iets mis bij het laden. Probeer de pagina te vernieuwen."
            }
          />
        </div>
      </div>
    );
  }

  const doneCount = items.filter((it) => it.state === "done").length;
  const failedItems = items.filter((it) => it.state === "error" || it.state === "rejected");

  return (
    <div className="min-h-screen bg-bg-base">
      <div className="max-w-md mx-auto px-5 py-8">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-2 mb-1">
            <Camera size={18} className="text-accent" />
            <span className="text-xs font-semibold tracking-wider uppercase text-accent">Foto's uploaden</span>
          </div>
          <h1 className="text-xl font-heading font-bold mb-1">{session.venueName || "Uw locatie"}</h1>
          <p className="text-sm text-text-secondary mb-6">
            Maak direct een foto of kies foto's uit uw galerij. Ze worden meteen toegevoegd aan de locatie.
          </p>

          {linkExpired && (
            <div className="mb-4">
              <Message
                variant="warning"
                text="Deze uploadlink is verlopen. Vraag de locatie-eigenaar om een nieuwe link; al toegevoegde foto's blijven bewaard."
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <button
              type="button"
              disabled={linkExpired}
              onClick={() => cameraInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 py-6 rounded-sm border-2 border-dashed border-border hover:border-accent hover:bg-accent-subtle transition-all duration-fast disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:bg-transparent"
            >
              <Camera size={26} strokeWidth={1.5} className="text-accent" />
              <span className="text-sm font-medium text-text-primary">Maak een foto</span>
            </button>
            <button
              type="button"
              disabled={linkExpired}
              onClick={() => galleryInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 py-6 rounded-sm border-2 border-dashed border-border hover:border-accent hover:bg-accent-subtle transition-all duration-fast disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:bg-transparent"
            >
              <Images size={26} strokeWidth={1.5} className="text-accent" />
              <span className="text-sm font-medium text-text-primary">Kies foto's</span>
            </button>
          </div>

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              onFilesSelected(e.target.files);
              e.target.value = "";
            }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              onFilesSelected(e.target.files);
              e.target.value = "";
            }}
          />

          {items.length > 0 && (
            <div className="mt-2">
              {doneCount > 0 && (
                <div className="flex items-center gap-2 mb-3 text-sm text-success font-medium">
                  <Check size={16} /> {doneCount} foto{doneCount === 1 ? "" : "'s"} toegevoegd
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                {items.map((it) => {
                  const failed = it.state === "error" || it.state === "rejected";
                  return (
                    <div key={it.key} className="relative aspect-square rounded-sm overflow-hidden bg-bg-sunken">
                      <img
                        src={it.preview}
                        alt=""
                        className={`w-full h-full object-cover ${failed ? "opacity-40" : ""}`}
                      />
                      {(it.state === "uploading" || it.state === "checking") && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <div className="flex flex-col items-center gap-1">
                            <Upload size={16} className="text-white" />
                            <span className="text-[11px] text-white font-medium">{it.progress}%</span>
                          </div>
                        </div>
                      )}
                      {it.state === "done" && (
                        <div className="absolute top-1 right-1 bg-success text-white rounded-full p-0.5">
                          <Check size={12} strokeWidth={3} />
                        </div>
                      )}
                      {failed && (
                        <>
                          <div
                            className={`absolute inset-0 ring-2 ring-inset rounded-sm pointer-events-none ${
                              it.state === "error" ? "ring-error/70" : "ring-warning/70"
                            }`}
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span
                              className={`flex h-8 w-8 items-center justify-center rounded-full text-white ${
                                it.state === "error" ? "bg-error" : "bg-warning"
                              }`}
                            >
                              {it.state === "error" ? (
                                <AlertCircle size={16} strokeWidth={2} />
                              ) : (
                                <AlertTriangle size={16} strokeWidth={2} />
                              )}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {failedItems.length > 0 && (
                <div className="mt-4 space-y-2">
                  {failedItems.map((it) => {
                    const isError = it.state === "error";
                    return (
                      <div
                        key={it.key}
                        className={`flex items-start gap-3 rounded-sm border p-3 ${
                          isError ? "border-error/20 bg-error-subtle" : "border-warning/20 bg-warning-subtle"
                        }`}
                      >
                        <span className={`shrink-0 mt-0.5 ${isError ? "text-error" : "text-warning"}`}>
                          {isError ? (
                            <AlertCircle size={18} strokeWidth={1.5} />
                          ) : (
                            <AlertTriangle size={18} strokeWidth={1.5} />
                          )}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-semibold ${isError ? "text-error" : "text-warning"}`}>
                            {isError ? "Uploaden mislukt" : "Foto niet toegevoegd"}
                          </p>
                          <p className="text-sm text-text-secondary leading-snug">{it.error}</p>
                          <div className="mt-2 flex items-center gap-4">
                            {isError ? (
                              <button
                                type="button"
                                disabled={linkExpired}
                                onClick={() => retryItem(it.key)}
                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                              >
                                <RotateCcw size={14} /> Opnieuw proberen
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={linkExpired}
                                onClick={() => galleryInputRef.current?.click()}
                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline disabled:opacity-50 disabled:no-underline"
                              >
                                <ImagePlus size={14} /> Andere foto kiezen
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeItem(it.key)}
                              className="text-sm text-text-tertiary hover:text-text-primary transition-colors duration-fast"
                            >
                              Verwijderen
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-text-tertiary mt-6 leading-relaxed">
            Gebruik bij voorkeur foto's van minimaal 1600×800 pixels. U kunt deze pagina sluiten zodra uw foto's zijn
            toegevoegd. De locatie-eigenaar ziet ze direct.
          </p>
        </div>
      </div>
    </div>
  );
}

export default UploadPhotos;
